import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { MapPin, Plus, QrCode, Trash, PencilSimple, ArrowsClockwise, Money, Clock } from "@phosphor-icons/react";
import { Section, Tile, Pill, Spinner, fmtDT, err } from "./hrUtils";
import { TONE_CLASSES } from "../assets/assetUtils";

const fmtH = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
const weekStart = () => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export function AttendanceTab() {
  const [from, setFrom] = useState(weekStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const load = useCallback(() => API.get("/hr/time", { params: { from_date: from, to_date: to } }).then((r) => setData(r.data)).catch(() => setData({ entries: [], summary: [], sites: [] })), [from, to]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Spinner />;
  const open = data.entries.filter((e) => e.status === "open");
  return (
    <div className="space-y-5" data-testid="hr-attendance">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Clocked in now" value={data.open_now} testid="hr-att-open" />
        <Tile label="Hours in range" value={(data.total_minutes / 60).toFixed(1)} testid="hr-att-hours" />
        <Tile label="People with time" value={data.summary.length} testid="hr-att-people" />
        <Tile label="Sites" value={data.sites.length} testid="hr-att-sites" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="On site right now" icon={Clock} testid="hr-att-live">
            {open.length === 0 ? <p className="text-sm text-muted-foreground">Nobody is clocked in.</p> : (
              <ul className="divide-y divide-border text-sm">{open.map((e) => (
                <li key={e.id} className="py-2 flex items-center justify-between gap-2">
                  <div><Link to={`/hr/people/${e.employee_id}`} className="font-medium hover:underline">{e.employee_name}</Link><p className="text-xs text-muted-foreground">{e.site_name} · since {fmtDT(e.clock_in.at)} · {e.clock_in.method === "qr" ? "QR" : "manual"} · {e.clock_in.distance_m} m from site</p></div>
                  <Pill tone={TONE_CLASSES.ok}>{fmtH(e.minutes)}</Pill>
                </li>
              ))}</ul>
            )}
          </Section>

          <Section title="Hours by person" icon={Money} testid="hr-att-summary" action={
            <div className="flex items-center gap-1">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 w-[8.5rem] text-xs" data-testid="hr-att-from" />
              <span className="text-xs text-muted-foreground">→</span>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-7 w-[8.5rem] text-xs" data-testid="hr-att-to" />
              <PushToRun from={from} to={to} />
            </div>
          }>
            {data.summary.length === 0 ? <p className="text-sm text-muted-foreground">No clock-ins in this range.</p> : (
              <table className="w-full text-sm"><thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Person</th><th>Days</th><th>Hours</th><th>Payroll</th></tr></thead>
                <tbody>{data.summary.map((s) => <tr key={s.employee_id} className="border-t border-border"><td className="py-1.5"><Link to={`/hr/people/${s.employee_id}`} className="hover:underline">{s.employee_name}</Link>{s.open && <span className="ml-1 text-[10px] text-emerald-600">● live</span>}</td><td>{s.days}</td><td className="font-medium">{s.hours}</td><td className="text-xs">{s.payroll_employee_id ? <Pill tone={TONE_CLASSES.ok}>Linked</Pill> : <span className="text-muted-foreground">Not linked</span>}</td></tr>)}</tbody>
              </table>
            )}
          </Section>

          <Section title="Entries" icon={Clock} testid="hr-att-entries">
            {data.entries.length === 0 ? <p className="text-sm text-muted-foreground">Nothing recorded.</p> : (
              <ul className="divide-y divide-border text-sm max-h-96 overflow-auto">{data.entries.map((e) => <Entry key={e.id} e={e} onChange={load} />)}</ul>
            )}
          </Section>
        </div>
        <SitesPanel sites={data.sites} onChange={load} />
      </div>
    </div>
  );
}

function Entry({ e, onChange }) {
  const [edit, setEdit] = useState(false);
  const [ci, setCi] = useState(e.clock_in.at.slice(0, 16));
  const [co, setCo] = useState(e.clock_out?.at?.slice(0, 16) || "");
  const [note, setNote] = useState(e.note || "");
  const save = async () => {
    try { await API.put(`/hr/time/${e.id}`, { clock_in_at: new Date(ci).toISOString(), clock_out_at: co ? new Date(co).toISOString() : "", note }); toast.success("Entry updated"); setEdit(false); onChange(); } catch (x) { err(x, "Couldn't update"); }
  };
  const remove = async () => { if (!window.confirm("Delete this entry?")) return; try { await API.delete(`/hr/time/${e.id}`); onChange(); } catch (x) { err(x, "Couldn't delete"); } };
  return (
    <li className="py-2" data-testid={`hr-att-entry-${e.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0"><p className="font-medium truncate">{e.employee_name} <span className="text-xs text-muted-foreground font-normal">· {e.site_name} · {e.date}</span></p>
          <p className="text-xs text-muted-foreground">{fmtDT(e.clock_in.at)} → {e.clock_out ? fmtDT(e.clock_out.at) : "open"} · {fmtH(e.minutes)}{e.adjusted_by ? ` · adjusted by ${e.adjusted_by}` : ""}{e.note ? ` · ${e.note}` : ""}</p></div>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => setEdit(!edit)} className="text-muted-foreground hover:text-foreground" aria-label="Adjust" data-testid={`hr-att-entry-${e.id}-edit`}><PencilSimple size={14} /></button>
          <button onClick={remove} className="text-red-500 hover:text-red-700" aria-label="Delete" data-testid={`hr-att-entry-${e.id}-delete`}><Trash size={14} /></button>
        </div>
      </div>
      {edit && (
        <div className="mt-2 grid sm:grid-cols-4 gap-2 items-end">
          <div><Label className="text-[11px]">In</Label><Input type="datetime-local" value={ci} onChange={(x) => setCi(x.target.value)} className="h-8 text-xs" data-testid={`hr-att-entry-${e.id}-in`} /></div>
          <div><Label className="text-[11px]">Out</Label><Input type="datetime-local" value={co} onChange={(x) => setCo(x.target.value)} className="h-8 text-xs" data-testid={`hr-att-entry-${e.id}-out`} /></div>
          <div><Label className="text-[11px]">Note</Label><Input value={note} onChange={(x) => setNote(x.target.value)} className="h-8 text-xs" data-testid={`hr-att-entry-${e.id}-note`} /></div>
          <Button size="sm" className="h-8" onClick={save} data-testid={`hr-att-entry-${e.id}-save`}>Save</Button>
        </div>
      )}
    </li>
  );
}

function PushToRun() {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState(null);
  useEffect(() => { if (open) API.get("/payroll/runs").then((r) => setRuns((r.data.runs || r.data || []).filter((x) => x.status === "draft"))).catch(() => setRuns([])); }, [open]);
  const push = async (rid) => { try { const r = await API.post(`/hr/time/push-to-run/${rid}`); toast.success(r.data.message); setOpen(false); } catch (e) { err(e, "Couldn't push hours"); } };
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid="hr-att-push-btn"><Money size={13} className="mr-1" /> To pay run</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="hr-att-push-dialog">
          <DialogHeader><DialogTitle>Push clocked hours to a draft pay run</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Hourly employees whose HR record is linked to payroll get their closed clock-ins for the run's period set as worked hours. Accounts roles only.</p>
          {!runs ? <Spinner /> : runs.length === 0 ? <p className="text-sm" data-testid="hr-att-push-empty">No draft pay runs — create one under Accounting › Payroll first.</p> : (
            <ul className="space-y-1">{runs.map((r) => <li key={r.id} className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"><span>{r.period_start} → {r.period_end} · pay {r.pay_date}</span><Button size="sm" onClick={() => push(r.id)} data-testid={`hr-att-push-${r.id}`}>Set hours</Button></li>)}</ul>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SitesPanel({ sites, onChange }) {
  const [editing, setEditing] = useState(null);
  const [qr, setQr] = useState(null);
  const showQr = async (s) => {
    try {
      const [png, link] = await Promise.all([API.get(`/hr/sites/${s.id}/qr.png`, { responseType: "blob" }), API.get(`/hr/sites/${s.id}/qr-link`)]);
      setQr({ site: s, src: URL.createObjectURL(png.data), url: link.data.url });
    } catch (e) { err(e, "Couldn't load QR"); }
  };
  const rotate = async (s) => { if (!window.confirm("Rotate the QR code? Printed posters stop working.")) return; try { const r = await API.post(`/hr/sites/${s.id}/rotate-qr`); toast.success(r.data.message); await showQr(s); } catch (e) { err(e, "Couldn't rotate"); } };
  const remove = async (s) => { if (!window.confirm(`Remove ${s.name}?`)) return; try { await API.delete(`/hr/sites/${s.id}`); onChange(); } catch (e) { err(e, "Couldn't remove site"); } };
  return (
    <Section title="Sites & QR posters" icon={MapPin} testid="hr-att-sites-panel" action={<Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing({})} data-testid="hr-site-add"><Plus size={13} className="mr-1" /> Site</Button>}>
      {sites.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="hr-sites-empty">Add the depot, office or job sites people clock in at. Each gets a radius and a printable QR poster.</p> : (
        <ul className="space-y-2">{sites.map((s) => (
          <li key={s.id} className="rounded-lg border border-border p-3 text-sm" data-testid={`hr-site-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><p className="font-medium flex items-center gap-1">{s.name}{!s.active && <Pill tone={TONE_CLASSES.none}>Closed</Pill>}</p>
                <p className="text-xs text-muted-foreground">{s.address || "No address"} · {s.radius_m} m radius{s.lat == null ? " · ⚠ no coordinates" : ""}</p></div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => showQr(s)} className="text-muted-foreground hover:text-foreground" aria-label="QR poster" data-testid={`hr-site-${s.id}-qr`}><QrCode size={16} /></button>
                <button onClick={() => setEditing(s)} className="text-muted-foreground hover:text-foreground" aria-label="Edit" data-testid={`hr-site-${s.id}-edit`}><PencilSimple size={16} /></button>
                <button onClick={() => remove(s)} className="text-red-500 hover:text-red-700" aria-label="Remove" data-testid={`hr-site-${s.id}-delete`}><Trash size={16} /></button>
              </div>
            </div>
          </li>
        ))}</ul>
      )}
      {editing && <SiteDialog site={editing} onClose={() => setEditing(null)} onSaved={onChange} />}
      <Dialog open={!!qr} onOpenChange={() => setQr(null)}>
        <DialogContent data-testid="hr-site-qr-dialog">
          <DialogHeader><DialogTitle>{qr?.site.name} — clock-in poster</DialogTitle></DialogHeader>
          {qr && <div className="text-center space-y-2"><img src={qr.src} alt="QR code" className="mx-auto w-64 h-64 rounded bg-white p-2" data-testid="hr-site-qr-img" /><p className="text-xs text-muted-foreground break-all">{qr.url}</p><p className="text-xs text-muted-foreground">Print and stick up on site. Scanning opens the clock page with this site chosen — the person still has to be within {qr.site.radius_m} m.</p></div>}
          <DialogFooter className="gap-2">
            {qr && <Button variant="outline" onClick={() => rotate(qr.site)} data-testid="hr-site-qr-rotate"><ArrowsClockwise size={14} className="mr-1" /> Rotate code</Button>}
            {qr && <a href={qr.src} download={`${qr.site.name}-clock-in-qr.png`}><Button data-testid="hr-site-qr-download">Download PNG</Button></a>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function SiteDialog({ site, onClose, onSaved }) {
  const [f, setF] = useState({ name: site.name || "", address: site.address || "", lat: site.lat ?? "", lng: site.lng ?? "", radius_m: site.radius_m || 150, active: site.active !== false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const useMyLocation = () => navigator.geolocation?.getCurrentPosition((p) => setF({ ...f, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }), () => toast.error("Couldn't get your location"));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, lat: f.lat === "" ? null : f.lat, lng: f.lng === "" ? null : f.lng };
      const r = site.id ? await API.put(`/hr/sites/${site.id}`, body) : await API.post("/hr/sites", body);
      toast.success(r.data.message); onSaved(); onClose();
    } catch (e) { err(e, "Couldn't save site"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="hr-site-dialog">
        <DialogHeader><DialogTitle>{site.id ? "Edit site" : "New site"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={set("name")} placeholder="Main depot" data-testid="hr-site-name" /></div>
          <div><Label className="text-xs">Address <span className="text-muted-foreground">(geocoded if you leave the coordinates blank)</span></Label><Input value={f.address} onChange={set("address")} data-testid="hr-site-address" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Latitude</Label><Input value={f.lat} onChange={set("lat")} data-testid="hr-site-lat" /></div>
            <div><Label className="text-xs">Longitude</Label><Input value={f.lng} onChange={set("lng")} data-testid="hr-site-lng" /></div>
            <div><Label className="text-xs">Radius (m)</Label><Input type="number" value={f.radius_m} onChange={set("radius_m")} data-testid="hr-site-radius" /></div>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={useMyLocation} data-testid="hr-site-use-location"><MapPin size={14} className="mr-1" /> Use my location</Button>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={set("active")} data-testid="hr-site-active" /> Open for clock-ins</label>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="hr-site-save">{busy ? "Saving…" : "Save site"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
